import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { tagLabel, TAG_ORDER } from "@/i18n/portfolioTags";

/* ============================================================
   Editor de proyecto a pagina completa.
   Izquierda: la ficha tal como se ve en el sitio (en vivo).
   Derecha: los campos, faciles de llenar. Lo tecnico queda
   plegado en "Avanzado" para que no estorbe.
   ============================================================ */

const LANGS = ["ru", "en", "es"];
const VIMEO_OPTS = "title=0&byline=0&portrait=0&badge=0&autopause=0&dnt=1&loop=1";

// mismo criterio de embed que usa el sitio publico (PortfolioGrid)
function toEmbed(url) {
  if (!url) return "";
  const u = String(url).trim();
  const vm = u.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\?([^#\s]*))?/);
  if (vm) {
    // mismo criterio que el sitio publico: el hash puede venir en cualquier
    // posicion y con &amp;, asi que normalizo antes de leerlo
    const params = new URLSearchParams((vm[2] || "").replace(/&amp;/g, "&"));
    const h = params.get("h");
    return `https://player.vimeo.com/video/${vm[1]}?${h ? `h=${h}&` : ""}${VIMEO_OPTS}`;
  }
  const yt = u.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{6,})/);
  if (yt && !u.includes("vimeo")) return `https://www.youtube.com/embed/${yt[1]}?rel=0&loop=1&playlist=${yt[1]}`;
  return u;
}

// ============================================================
//  SUBIDA DE IMÁGENES
//  El servidor NO tiene librería de imágenes (a propósito), así
//  que el navegador achica y comprime antes de mandar. Igual que
//  el avatar. Los GIF se mandan tal cual: si pasaran por canvas
//  se perdería la animación y quedaría solo el primer cuadro.
// ============================================================
const MAX_LADO = 2200;

function comprimir(file) {
  return new Promise((resolve) => {
    if (!file || file.type === "image/gif") { resolve(file); return; }
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const escala = Math.min(1, MAX_LADO / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * escala));
      const h = Math.max(1, Math.round(img.height * escala));
      const c = document.createElement("canvas");
      c.width = w; c.height = h;
      c.getContext("2d").drawImage(img, 0, 0, w, h);
      const tipo = file.type === "image/png" ? "image/png" : "image/jpeg";
      c.toBlob(
        (b) => resolve(b ? new File([b], (file.name || "imagen").replace(/\.[^.]+$/, "") + (tipo === "image/png" ? ".png" : ".jpg"), { type: tipo }) : file),
        tipo,
        0.88
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

async function subir(file) {
  const f = await comprimir(file);
  const fd = new FormData();
  fd.append("file", f, f.name || "imagen.jpg");
  const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
}

// Une 2-4 imágenes en una sola tira horizontal. Sirve para el material vertical:
// tres tiras 9:16 lado a lado llenan el ancho de la viñeta (16:9) sin recortar nada.
function combinar(pares) {
  const cargar = (p) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("No pude leer " + (p.file?.name || "una imagen")));
    img.src = p.url;
  });
  return Promise.all(pares.map(cargar)).then((imgs) => {
    const alto = Math.max(...imgs.map((i) => i.height));
    const anchos = imgs.map((i) => Math.max(1, Math.round(i.width * (alto / i.height))));
    const ancho = anchos.reduce((a, b) => a + b, 0);
    const c = document.createElement("canvas");
    c.width = ancho; c.height = alto;
    const ctx = c.getContext("2d");
    // fondo negro: si las piezas no miden lo mismo, rellena sin dejar transparencia
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, ancho, alto);
    let x = 0;
    imgs.forEach((img, i) => { ctx.drawImage(img, x, 0, anchos[i], alto); x += anchos[i]; });
    return new Promise((resolve) => c.toBlob((b) => resolve(new File([b], "combinada.jpg", { type: "image/jpeg" })), "image/jpeg", 0.9));
  });
}

// Zona para soltar archivos, como el "arrastrá acá" de un correo.
function ZonaArrastre({ onFiles, etiqueta, subetiqueta, compacto, multiple = true }) {
  const [encima, setEncima] = useState(false);
  const inputRef = useRef(null);
  const pintar = (lista) => {
    const fs = Array.from(lista || []).filter((f) => (f.type || "").startsWith("image/"));
    if (fs.length) onFiles(fs);
  };
  return (
    <div
      onDragEnter={(e) => { e.preventDefault(); setEncima(true); }}
      onDragOver={(e) => { e.preventDefault(); setEncima(true); }}
      onDragLeave={() => setEncima(false)}
      onDrop={(e) => { e.preventDefault(); setEncima(false); pintar(e.dataTransfer?.files); }}
      onClick={() => inputRef.current?.click()}
      data-testid="zona-arrastre"
      className={`cursor-pointer border border-dashed text-center transition-colors ${
        compacto ? "px-3 py-4" : "px-4 py-7"
      } ${encima ? "border-[#00d4ff] bg-[#00d4ff]/10" : "border-white/25 bg-white/[0.02] hover:border-white/50"}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        hidden
        onChange={(e) => { pintar(e.target.files); e.target.value = ""; }}
      />
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/70">{etiqueta}</div>
      {subetiqueta && <div className="mt-1 font-mono text-[10px] text-white/35">{subetiqueta}</div>}
    </div>
  );
}

const VACIO = {
  title: { ru: "", en: "", es: "" },
  category: { ru: "", en: "", es: "" },
  description: { ru: "", en: "", es: "" },
  tags: [],
  thumbnail: "",
  video_url: "",
  video_url_2: "",
  video_duration: "",
  video_duration_2: "",
  client: "",
  year: new Date().getFullYear(),
  duration: "",
  tools: [],
  process_images: [],
  process_titles: [],
  color_a: "#ff006e",
  color_b: "#00d4ff",
  status: "published",
  order: 0,
};

const Meta = ({ label, value }) => (
  <div>
    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8a8a8a]">{label}</div>
    <div className="mt-1 font-sans text-sm text-[#0a0a0a]">{value || "—"}</div>
  </div>
);

const Campo = ({ titulo, children }) => (
  <div className="border border-white/10 p-4 bg-white/[0.02]">
    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mb-3">{titulo}</div>
    {children}
  </div>
);

const inputCls =
  "w-full px-3 py-2 bg-transparent border border-white/20 text-white font-sans text-sm focus:outline-none focus:border-white/60";

export default function AdminPortfolioEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const esNuevo = id === "new";

  const [form, setForm] = useState(esNuevo ? VACIO : null);
  const [lang, setLang] = useState("ru");
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState("");
  const [err, setErr] = useState("");
  const [avanzado, setAvanzado] = useState(false);
  const [herramienta, setHerramienta] = useState("");

  useEffect(() => {
    let vivo = true;
    (async () => {
      if (esNuevo) return;
      try {
        const { data } = await api.get("/admin/portfolio");
        const item = (data || []).find((p) => p.project_id === id);
        if (!vivo) return;
        if (item) setForm(item);
        else setErr("No encontré ese proyecto.");
      } catch (e) {
        if (vivo) setErr("No se pudo cargar el proyecto.");
      }
    })();
    return () => { vivo = false; };
  }, [id, esNuevo]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setI18n = (k, v) => setForm((f) => ({ ...f, [k]: { ...(f[k] || {}), [lang]: v } }));

  const traducir = async (campo) => {
    const actual = (form[campo]?.[lang] || "").trim();
    if (!actual) { setErr("Escribí primero el texto en este idioma."); return; }
    setBusy(true); setErr(""); setOk("");
    try {
      const { data } = await api.post("/admin/translate", { text: actual, from_lang: lang });
      set(campo, {
        en: data.en || form[campo]?.en || "",
        ru: data.ru || form[campo]?.ru || "",
        es: data.es || form[campo]?.es || "",
      });
      setOk("Traducido. Revisá los otros idiomas antes de guardar.");
    } catch (e) {
      setErr("No se pudo traducir.");
    } finally { setBusy(false); }
  };

  const guardar = async () => {
    setBusy(true); setErr(""); setOk("");
    try {
      const payload = { ...form };
      delete payload.project_id;
      delete payload.created_at;
      delete payload.updated_at;
      if (esNuevo) await api.post("/admin/portfolio", payload);
      else await api.put(`/admin/portfolio/${id}`, payload);
      setOk("Guardado ✔");
    } catch (e) {
      setErr(e?.response?.data?.detail ? String(e.response.data.detail) : "No se pudo guardar.");
    } finally { setBusy(false); }
  };

  const alternarTag = (tg) =>
    set("tags", (form.tags || []).includes(tg)
      ? (form.tags || []).filter((t) => t !== tg)
      : [...(form.tags || []), tg]);

  const agregarHerramienta = () => {
    const v = herramienta.trim();
    if (!v) return;
    if (!(form.tools || []).includes(v)) set("tools", [...(form.tools || []), v]);
    setHerramienta("");
  };

  // ---- reordenar las piezas del proceso arrastrando ----
  const [arrastra, setArrastra] = useState(null);
  const [sobre, setSobre] = useState(null);

  // ---- subida de imágenes ----
  const [subiendo, setSubiendo] = useState(0);
  const [comb, setComb] = useState([]);   // [{file, url}] para el combinador
  const [dropZona, setDropZona] = useState(null); // "portada" | "piezas" | null

  // ---- soltar archivos DIRECTAMENTE sobre la vista previa ----
  const archivosImagen = (dt) =>
    Array.from(dt?.files || []).filter((f) => (f.type || "").startsWith("image/"));

  const enlaceVideo = (dt) => {
    let txt = "";
    try { txt = (dt?.getData?.("text/uri-list") || dt?.getData?.("text/plain") || "").trim(); } catch (e) { txt = ""; }
    return /vimeo\.com|youtu\.?be/i.test(txt) ? txt : "";
  };

  // en dragover no se puede leer el contenido, solo los tipos: sirve para saber
  // si vale la pena iluminar la zona
  const hayAlgoQueSoltar = (e) => {
    const t = Array.from(e.dataTransfer?.types || []);
    return t.includes("Files") || t.includes("text/uri-list") || t.includes("text/plain");
  };

  // soltar sobre el marco grande: si es una imagen -> portada; si es un link -> video
  const soltarEnPortada = (e) => {
    e.preventDefault();
    setDropZona(null);
    const fs = archivosImagen(e.dataTransfer);
    if (fs.length) { subirPortada(fs); return; }
    const enlace = enlaceVideo(e.dataTransfer);
    if (enlace) {
      set("video_url", enlace);
      setOk("Video cargado desde el enlace. Acordate de guardar.");
    }
  };

  // soltar sobre la tira del proceso: agrega viñetas nuevas.
  // Si no vienen archivos (es una pieza que se está moviendo), no hace nada acá.
  const soltarEnPiezas = (e) => {
    const fs = archivosImagen(e.dataTransfer);
    if (!fs.length) return;
    e.preventDefault();
    setDropZona(null);
    subirPiezas(fs);
  };

  const nuevasPiezas = (urls) => setForm((f) => {
    const imgs = [...(f.process_images || [])];
    const tits = [...(f.process_titles || [])];
    // las dos listas tienen que quedar siempre del mismo largo: si no, los
    // títulos se corren respecto de las imágenes al guardar
    while (tits.length < imgs.length) tits.push({ ru: "", en: "", es: "" });
    urls.forEach((u) => { imgs.push(u); tits.push({ ru: "", en: "", es: "" }); });
    return { ...f, process_images: imgs, process_titles: tits };
  });

  const subirPiezas = async (files) => {
    setBusy(true); setErr(""); setOk("");
    try {
      const urls = [];
      for (const f of files) {
        setSubiendo((n) => n + 1);
        urls.push(await subir(f));
      }
      nuevasPiezas(urls);
      setOk(`${urls.length} viñeta(s) subida(s). Acordate de guardar.`);
    } catch (e) {
      setErr("No se pudo subir la imagen (¿pesa más de 8 MB?).");
    } finally { setSubiendo(0); setBusy(false); }
  };

  const subirPortada = async (files) => {
    if (!files[0]) return;
    setBusy(true); setErr(""); setOk("");
    try {
      const url = await subir(files[0]);
      set("thumbnail", url);
      setOk("Portada subida. Acordate de guardar.");
    } catch (e) {
      setErr("No se pudo subir la portada.");
    } finally { setBusy(false); }
  };

  const agregarAlCombinador = (files) =>
    setComb((c) => [...c, ...files.map((f) => ({ file: f, url: URL.createObjectURL(f) }))].slice(0, 4));

  const quitarDelCombinador = (i) => setComb((c) => {
    const p = c[i];
    if (p?.url) URL.revokeObjectURL(p.url);
    return c.filter((_, k) => k !== i);
  });

  const unirYAgregar = async () => {
    if (comb.length < 2) return;
    setBusy(true); setErr(""); setOk("");
    try {
      const url = await subir(await combinar(comb));
      nuevasPiezas([url]);
      comb.forEach((p) => URL.revokeObjectURL(p.url));
      setComb([]);
      setOk("Imagen unida y agregada como una sola viñeta. Acordate de guardar.");
    } catch (e) {
      setErr("No se pudieron unir las imágenes.");
    } finally { setBusy(false); }
  };

  // título propio de cada viñeta, en el idioma que esté elegido arriba
  const tituloPieza = (i, v) => setForm((f) => {
    const tits = [...(f.process_titles || [])];
    while (tits.length <= i) tits.push({ ru: "", en: "", es: "" });
    const actual = (tits[i] && typeof tits[i] === "object") ? { ...tits[i] } : { ru: "", en: "", es: "" };
    actual[lang] = v;
    tits[i] = actual;
    return { ...f, process_titles: tits };
  });

  const traducirPieza = async (i) => {
    const actual = (((form.process_titles || [])[i] || {})[lang] || "").trim();
    if (!actual) { setErr("Escribí primero el nombre de la viñeta en este idioma."); return; }
    setBusy(true); setErr(""); setOk("");
    try {
      const { data } = await api.post("/admin/translate", { text: actual, from_lang: lang });
      const tits = [...(form.process_titles || [])];
      while (tits.length <= i) tits.push({ ru: "", en: "", es: "" });
      tits[i] = { ...(tits[i] || {}), en: data.en || "", ru: data.ru || "", es: data.es || "" };
      setForm((f) => ({ ...f, process_titles: tits }));
      setOk("Viñeta traducida a los 3 idiomas.");
    } catch (e) {
      setErr("No se pudo traducir la viñeta.");
    } finally { setBusy(false); }
  };

  const moverPieza = (desde, hasta) => {
    if (desde === null || hasta === null || desde === hasta) return;
    const imgs = [...(form.process_images || [])];
    const tits = [...(form.process_titles || [])];
    if (desde < 0 || desde >= imgs.length) return;
    const [img] = imgs.splice(desde, 1);
    const [tit] = tits.splice(desde, 1);
    imgs.splice(hasta, 0, img);
    if (tit !== undefined) tits.splice(hasta, 0, tit);
    setForm((f) => ({ ...f, process_images: imgs, process_titles: tits }));
  };

  if (!form) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/60">
          {err || "cargando…"}
        </div>
      </div>
    );
  }

  const preview = form;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* barra superior */}
      <div className="sticky top-0 z-20 border-b border-white/10 bg-[#0a0a0a]/95 backdrop-blur">
        <div className="px-6 py-4 flex flex-wrap items-center gap-4">
          <button
            onClick={() => navigate("/dashboard")}
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 hover:text-white"
          >
            ← volver
          </button>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-white">
            {esNuevo ? "nuevo proyecto" : "editar proyecto"}
          </span>

          <div className="ml-auto flex items-center gap-2">
            {/* idioma de todo el formulario */}
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mr-1">
              idioma
            </span>
            {LANGS.map((L) => (
              <button
                key={L}
                onClick={() => setLang(L)}
                data-testid={`form-lang-${L}`}
                className={`px-3 py-1.5 border font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${
                  lang === L
                    ? "border-white/70 text-white bg-white/15"
                    : "border-white/20 text-white/50 hover:text-white/90"
                }`}
              >
                {L}
              </button>
            ))}
            <button
              onClick={guardar}
              disabled={busy}
              data-testid="guardar"
              className="ml-2 px-5 py-1.5 bg-white text-[#0a0a0a] font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-white/90 disabled:opacity-50"
            >
              {busy ? "guardando…" : "guardar"}
            </button>
          </div>
        </div>
        {(ok || err) && (
          <div className={`px-6 pb-3 font-mono text-[11px] ${err ? "text-red-400" : "text-[#00d4ff]"}`}>
            {err || ok}
          </div>
        )}
      </div>

      {/* la vista previa se limita a un ancho parecido al de la ficha real,
          asi no se estira gigante en pantallas anchas */}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,720px)_420px] gap-6 p-6 xl:justify-center">
        {/* ---------- IZQUIERDA: la ficha como se vera ---------- */}
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">
            así se verá en el sitio
          </div>
          <div className="bg-white border border-[#e5e5e5]">
            <div
              data-testid="soltar-portada"
              className={`aspect-[16/9] bg-black relative overflow-hidden transition-all ${
                dropZona === "portada" ? "ring-2 ring-inset ring-[#00d4ff]" : ""
              }`}
              onDragEnter={(e) => { if (hayAlgoQueSoltar(e)) { e.preventDefault(); setDropZona("portada"); } }}
              onDragOver={(e) => { if (hayAlgoQueSoltar(e)) { e.preventDefault(); setDropZona("portada"); } }}
              onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDropZona(null); }}
              onDrop={soltarEnPortada}
            >
              {preview.video_url ? (
                <iframe
                  title="vista previa"
                  src={toEmbed(preview.video_url)}
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                />
              ) : preview.thumbnail ? (
                <img src={preview.thumbnail} alt="" className="w-full h-full object-contain" />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center">
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
                    sin video ni portada
                  </div>
                  <div className="font-mono text-[10px] text-white/25 leading-relaxed max-w-xs">
                    arrastrá una imagen acá y queda de portada · o pegá el link de Vimeo / YouTube
                  </div>
                </div>
              )}
              {dropZona === "portada" && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#00d4ff]/15 pointer-events-none">
                  <div className="px-4 py-2 bg-[#0a0a0a]/85 border border-[#00d4ff] font-mono text-[10px] uppercase tracking-[0.25em] text-[#00d4ff]">
                    soltá para ponerla de portada
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 lg:p-8">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8a8a8a]">
                {preview.category?.[lang] || "categoría"}
              </div>
              <h3 className="mt-2 font-mono font-bold tracking-tighter text-3xl lg:text-4xl text-[#0a0a0a]">
                {preview.title?.[lang] || "título del proyecto"}
              </h3>
              <p className="mt-6 font-sans text-base text-[#4a4a4a] leading-relaxed max-w-2xl">
                {preview.description?.[lang] || "Acá va la descripción del caso."}
              </p>

              <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-6">
                <Meta label="cliente" value={preview.client} />
                <Meta label="año" value={preview.year} />
                <Meta
                  label="duración"
                  value={
                    preview.video_duration && preview.video_duration_2
                      ? `INTRO ${preview.video_duration} · OUTRO ${preview.video_duration_2}`
                      : preview.duration
                  }
                />
                <Meta label="herramientas" value={(preview.tools || []).join(", ")} />
              </div>

              <div className="mt-10 flex flex-wrap gap-2">
                {(preview.tags || []).map((tg) => (
                  <span
                    key={tg}
                    className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#0a0a0a] border border-[#0a0a0a] px-3 py-1.5"
                  >
                    #{tagLabel(tg, lang)}
                  </span>
                ))}
              </div>

              <div
                data-testid="soltar-piezas"
                className={`mt-10 pt-6 border-t transition-colors ${
                  dropZona === "piezas" ? "border-[#00d4ff] bg-[#00d4ff]/5" : "border-[#e5e5e5]"
                }`}
                onDragEnter={(e) => { if (archivosImagen(e.dataTransfer).length) { e.preventDefault(); setDropZona("piezas"); } }}
                onDragOver={(e) => { if (archivosImagen(e.dataTransfer).length) { e.preventDefault(); setDropZona("piezas"); } }}
                onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDropZona(null); }}
                onDrop={soltarEnPiezas}
              >
                  <div className="flex items-baseline justify-between gap-4">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8a8a8a]">
                      proceso · {(form.process_images || []).length} piezas
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#b5b5b5]">
                      {(form.process_images || []).length > 0 ? "arrastrá para reordenar" : "soltá las imágenes acá"}
                    </div>
                  </div>
                  {(form.process_images || []).length === 0 && (
                    <div className="mt-3 border border-dashed border-[#c9c9c9] px-6 py-10 text-center">
                      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8a8a8a]">
                        soltá las viñetas acá
                      </div>
                      <div className="mt-2 font-mono text-[10px] text-[#b5b5b5] leading-relaxed">
                        podés soltar varias juntas y quedan en el orden en que las sueltes
                      </div>
                    </div>
                  )}
                  <div className="mt-3 grid grid-cols-3 lg:grid-cols-4 gap-2">
                    {(form.process_images || []).map((u, i) => (
                      <div
                        key={`${u}-${i}`}
                        draggable
                        onDragStart={() => setArrastra(i)}
                        onDragOver={(e) => { e.preventDefault(); setSobre(i); }}
                        onDrop={(e) => { e.preventDefault(); moverPieza(arrastra, i); setArrastra(null); setSobre(null); }}
                        onDragEnd={() => { setArrastra(null); setSobre(null); }}
                        data-testid={`pieza-${i}`}
                        title="arrastrá para cambiarla de lugar"
                        className={`group relative cursor-move border-2 bg-[#f2f2f2] transition-all ${
                          sobre === i && arrastra !== null ? "border-[#00d4ff]" : "border-transparent"
                        } ${arrastra === i ? "opacity-40" : ""}`}
                      >
                        <img src={u} alt="" className="w-full aspect-[16/9] object-cover pointer-events-none" />
                        <span className="absolute top-1 left-1 px-1.5 py-0.5 bg-[#0a0a0a]/80 font-mono text-[10px] text-white">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <div className="absolute bottom-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            type="button"
                            onClick={() => moverPieza(i, i - 1)}
                            disabled={i === 0}
                            className="w-6 h-6 bg-[#0a0a0a]/80 text-white font-mono text-xs disabled:opacity-30"
                            title="mover antes"
                          >
                            ←
                          </button>
                          <button
                            type="button"
                            onClick={() => moverPieza(i, i + 1)}
                            disabled={i === (form.process_images || []).length - 1}
                            className="w-6 h-6 bg-[#0a0a0a]/80 text-white font-mono text-xs disabled:opacity-30"
                            title="mover después"
                          >
                            →
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setForm((f) => ({
                                ...f,
                                process_images: (f.process_images || []).filter((_, k) => k !== i),
                                process_titles: (f.process_titles || []).filter((_, k) => k !== i),
                              }))
                            }
                            className="w-6 h-6 bg-[#0a0a0a]/80 text-white font-mono text-xs hover:bg-red-600"
                            title="quitar del carrusel"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="mt-2 font-mono text-[10px] text-[#b5b5b5]">
                    {(form.process_images || []).length > 0
                      ? "el orden se guarda con el proyecto (botón guardar)"
                      : "también podés usar el bloque «viñetas del proceso» de la derecha"}
                  </p>
              </div>
            </div>
          </div>
        </div>

        {/* ---------- DERECHA: los campos ---------- */}
        <div className="space-y-4">
          <Campo titulo={`título · ${lang}`}>
            <div className="flex gap-2">
              <input
                className={inputCls}
                value={form.title?.[lang] || ""}
                onChange={(e) => setI18n("title", e.target.value)}
                data-testid="campo-title"
              />
              <button
                onClick={() => traducir("title")}
                disabled={busy}
                className="shrink-0 px-3 py-2 border border-[#00d4ff]/40 text-[#00d4ff] font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#00d4ff]/10 disabled:opacity-50"
              >
                traducir
              </button>
            </div>
          </Campo>

          <Campo titulo={`categoría · ${lang}`}>
            <div className="flex gap-2">
              <input
                className={inputCls}
                value={form.category?.[lang] || ""}
                onChange={(e) => setI18n("category", e.target.value)}
                data-testid="campo-category"
              />
              <button
                onClick={() => traducir("category")}
                disabled={busy}
                className="shrink-0 px-3 py-2 border border-[#00d4ff]/40 text-[#00d4ff] font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#00d4ff]/10 disabled:opacity-50"
              >
                traducir
              </button>
            </div>
          </Campo>

          <Campo titulo={`descripción · ${lang}`}>
            <div className="flex gap-2">
              <textarea
                rows={7}
                className={`${inputCls} resize-y`}
                value={form.description?.[lang] || ""}
                onChange={(e) => setI18n("description", e.target.value)}
                data-testid="campo-description"
              />
              <button
                onClick={() => traducir("description")}
                disabled={busy}
                className="shrink-0 px-3 py-2 border border-[#00d4ff]/40 text-[#00d4ff] font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#00d4ff]/10 disabled:opacity-50"
              >
                traducir
              </button>
            </div>
          </Campo>

          <div className="grid grid-cols-2 gap-4">
            <Campo titulo="cliente">
              <input className={inputCls} value={form.client || ""} onChange={(e) => set("client", e.target.value)} />
            </Campo>
            <Campo titulo="año">
              <input className={inputCls} type="number" value={form.year || ""} onChange={(e) => set("year", Number(e.target.value))} />
            </Campo>
            <Campo titulo="duración (ej. 0:45)">
              <input className={inputCls} value={form.duration || ""} onChange={(e) => set("duration", e.target.value)} />
            </Campo>
            <Campo titulo="estado">
              <select className={`${inputCls} bg-[#0a0a0a]`} value={form.status || "published"} onChange={(e) => set("status", e.target.value)}>
                <option value="published">publicado</option>
                <option value="draft">borrador (no se ve en el sitio)</option>
              </select>
            </Campo>
          </div>

          <Campo titulo="etiquetas (tocá para marcar)">
            <div className="flex flex-wrap gap-2">
              {TAG_ORDER.map((tg) => {
                const activo = (form.tags || []).includes(tg);
                return (
                  <button
                    key={tg}
                    onClick={() => alternarTag(tg)}
                    data-testid={`tag-${tg}`}
                    className={`px-3 py-1.5 border font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${
                      activo
                        ? "border-[#00d4ff] text-[#0a0a0a] bg-[#00d4ff]"
                        : "border-white/25 text-white/60 hover:text-white"
                    }`}
                  >
                    #{tagLabel(tg, "es")}
                  </button>
                );
              })}
            </div>
            <div className="mt-2 font-mono text-[10px] text-white/40">
              {(form.tags || []).length} seleccionada(s)
            </div>
          </Campo>

          <Campo titulo="herramientas">
            <div className="flex gap-2">
              <input
                className={inputCls}
                placeholder="After Effects, Blender…"
                value={herramienta}
                onChange={(e) => setHerramienta(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); agregarHerramienta(); } }}
              />
              <button
                onClick={agregarHerramienta}
                className="shrink-0 px-4 py-2 border border-white/25 font-mono text-[10px] uppercase tracking-[0.2em] text-white/70 hover:text-white"
              >
                sumar
              </button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {(form.tools || []).map((h) => (
                <span key={h} className="flex items-center gap-2 px-3 py-1 border border-white/20 font-mono text-[10px] text-white/80">
                  {h}
                  <button
                    onClick={() => set("tools", (form.tools || []).filter((x) => x !== h))}
                    className="text-white/40 hover:text-red-400"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </Campo>

          {subiendo > 0 && (
            <div className="font-mono text-[10px] text-[#00d4ff]">subiendo {subiendo} imagen(es)…</div>
          )}

          <Campo titulo="viñetas del proceso (las piezas del carrusel)">
            <p className="font-mono text-[10px] text-white/35 leading-relaxed">
              para agregar viñetas, soltá las imágenes sobre la tira «proceso» de la vista previa.
            </p>

            {/* unir dos o más imágenes en una sola viñeta (material vertical) */}
            <div className="mt-4 border border-white/10 p-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/45">
                unir imágenes lado a lado
              </div>
              <div className="mt-1 font-mono text-[10px] text-white/30 leading-relaxed">
                para material vertical: soltá 2 o 3 y quedan en una sola viñeta, sin recortar nada.
              </div>
              <div className="mt-2">
                <ZonaArrastre
                  compacto
                  onFiles={agregarAlCombinador}
                  etiqueta="soltá las imágenes a unir"
                  subetiqueta="después apretá “unir y agregar”"
                />
              </div>
              {comb.length > 0 && (
                <div className="mt-3 flex flex-wrap items-end gap-2">
                  {comb.map((p, i) => (
                    <button
                      key={`${p.url}-${i}`}
                      type="button"
                      onClick={() => quitarDelCombinador(i)}
                      title="quitar"
                      className="relative border border-white/30 hover:border-red-400"
                    >
                      <img src={p.url} alt="" className="h-16 w-auto" />
                      <span className="absolute -top-1.5 -right-1.5 w-4 h-4 leading-4 bg-[#0a0a0a] border border-white/40 font-mono text-[9px]">
                        ×
                      </span>
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={unirYAgregar}
                    disabled={comb.length < 2 || busy}
                    data-testid="unir-y-agregar"
                    className="px-3 py-2 bg-white text-[#0a0a0a] font-mono text-[10px] uppercase tracking-[0.2em] disabled:opacity-40"
                  >
                    unir y agregar
                  </button>
                </div>
              )}
            </div>

            {/* cada viñeta con su propio nombre en los 3 idiomas */}
            <div className="mt-4 space-y-2">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/45">
                nombre de cada viñeta · {lang}
              </div>
              {(form.process_images || []).map((u, i) => (
                <div key={`${u}-${i}`} className="flex items-center gap-2 border border-white/10 p-2">
                  <span className="w-5 shrink-0 font-mono text-[10px] text-white/50">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <img src={u} alt="" className="h-10 w-16 shrink-0 object-cover bg-white/5" />
                  <input
                    className={inputCls}
                    placeholder="ej. storyboard"
                    value={(((form.process_titles || [])[i] || {})[lang]) || ""}
                    onChange={(e) => tituloPieza(i, e.target.value)}
                    data-testid={`titulo-pieza-${i}`}
                  />
                  <button
                    type="button"
                    onClick={() => traducirPieza(i)}
                    disabled={busy}
                    title="traducir a los otros idiomas"
                    className="shrink-0 px-2 py-2 border border-[#00d4ff]/40 text-[#00d4ff] font-mono text-[10px] uppercase tracking-[0.15em] hover:bg-[#00d4ff]/10 disabled:opacity-40"
                  >
                    tr
                  </button>
                </div>
              ))}
              {(form.process_images || []).length === 0 && (
                <div className="font-mono text-[10px] text-white/30">todavía no hay viñetas.</div>
              )}
            </div>
            <p className="mt-2 font-mono text-[10px] text-white/30 leading-relaxed">
              el orden se cambia arrastrando las miniaturas de la vista previa.
            </p>
          </Campo>

          {/* lo tecnico, plegado */}
          <div className="border border-white/10 bg-white/[0.02]">
            <button
              onClick={() => setAvanzado((a) => !a)}
              data-testid="toggle-avanzado"
              className="w-full px-4 py-3 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 hover:text-white"
            >
              <span>avanzado · urls y datos técnicos</span>
              <span>{avanzado ? "−" : "+"}</span>
            </button>
            {avanzado && (
              <div className="px-4 pb-4 space-y-4">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">url miniatura</div>
                  <input className={inputCls} value={form.thumbnail || ""} onChange={(e) => set("thumbnail", e.target.value)} />
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">url video</div>
                  <input className={inputCls} value={form.video_url || ""} onChange={(e) => set("video_url", e.target.value)} />
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">url video 2 (outro)</div>
                  <input className={inputCls} value={form.video_url_2 || ""} onChange={(e) => set("video_url_2", e.target.value)} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">duración video 1</div>
                    <input className={inputCls} value={form.video_duration || ""} onChange={(e) => set("video_duration", e.target.value)} />
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">duración video 2</div>
                    <input className={inputCls} value={form.video_duration_2 || ""} onChange={(e) => set("video_duration_2", e.target.value)} />
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">orden</div>
                    <input className={inputCls} type="number" value={form.order || 0} onChange={(e) => set("order", Number(e.target.value))} />
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">piezas de proceso</div>
                    <input className={inputCls} value={(form.process_images || []).length} readOnly />
                  </div>
                </div>
                <p className="font-mono text-[10px] text-white/30 leading-relaxed">
                  las URLs son iguales para los 3 idiomas, por eso no cambian con el selector.
                  las piezas del proceso se cargan aparte.
                </p>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <button
              onClick={guardar}
              disabled={busy}
              className="flex-1 px-5 py-3 bg-white text-[#0a0a0a] font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-white/90 disabled:opacity-50"
            >
              {busy ? "guardando…" : "guardar cambios"}
            </button>
            <button
              onClick={() => navigate("/dashboard")}
              className="px-5 py-3 border border-white/25 font-mono text-[11px] uppercase tracking-[0.2em] text-white/70 hover:text-white"
            >
              volver
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
